import { LoginFlow } from "@omni/types/api/customer/bff/shared/enums/loginFlow.enum";
import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import queryString from "query-string";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { useCheckout } from "@/components/Checkout/useCheckout.hook";
import { useThunkDispatch } from "@/redux/dispatcher";
import { CheckoutStepValidationResult } from "@/redux/modules/api/data/cart/@types/cart.types";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import { isLoading } from "@/redux/modules/api/data/customer/customer.selectors";
import * as AccountFormsModule from "@/redux/modules/value/accountForms/accountForms.module";
import * as AccountFormsSelectors from "@/redux/modules/value/accountForms/accountForms.selectors";
import { getHasAccountModalUserData } from "@/redux/modules/value/hasAccountModalUserData/hasAccountModalUserData.selectors";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { EcornerService } from "@/services/eCorner.service";
import * as CheckoutStepTracker from "@/services/tracking/trackers/tools/checkout/checkoutStep.tracker";
import * as LoginStepTracker from "@/services/tracking/trackers/tools/login/loginStep.tracker";
import { CheckoutSteps, StorageKeys } from "@/utils/enums";

import { LoginForm } from "./loginForm.component";

export type LoginFormContainerProps = {
    title: string;
    buttonText: string;
    pageLogic: LoginFlow;
    linkTarget: string;
    linkSecondaryTarget: string;
};

const LoginFormContainer = ({
    pageLogic,
    linkTarget,
    linkSecondaryTarget,
    title,
    buttonText
}: LoginFormContainerProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const accountForms = useSelector(AccountFormsSelectors.getAccountForms());
    const isLoadingCustomer = useSelector(isLoading);
    const cart = useSelector(getCart);
    const hasAccountModalUserData = useSelector(getHasAccountModalUserData);

    const login = (email: string, password: string) => {
        return dispatch(CustomerModule.login(email, password));
    };

    const { validateCheckoutStep, cartLoading } = useCheckout();

    const handleSearchParams = useCallback(async () => {
        const { passwordsuccess, logoutsuccess, passwordupdated } =
            queryString.parse(location.search);

        if (passwordsuccess === "true") {
            broadcastMessage({
                id: "password-success",
                text: t("account.resetPassword.success"),
                status: "success",
                dismissible: true
            });
        }
        if (passwordupdated === "true") {
            broadcastMessage({
                id: "passwordupdated-success",
                text: t("account.updatePassword.success"),
                status: "success",
                dismissible: true
            });
        }
        if (logoutsuccess === "true") {
            broadcastMessage({
                id: "logout-success",
                text: `${t("account.logout.success")} <a href="${
                    window.location.origin
                }">${t("account.logout.success.continueShopping")}</a>`,
                status: "success",
                dismissible: true
            });
        }
        if (await EcornerService.isEcornerEnvironment()) {
            broadcastMessage({
                id: "ecorner-public",
                text: t("ecorner.loginForm.warning"),
                status: "warning",
                dismissible: true
            });
        }
    }, [t]);

    useEffect(() => {
        if (cart && pageLogic === LoginFlow.CHECKOUT) {
            CheckoutStepTracker.trackLoginStart(cart);
        }
    }, [cart, pageLogic]);

    useEffect(() => {
        handleSearchParams();
    }, [handleSearchParams]);

    const onUpdateCardToggle = (isOpen: boolean) => {
        dispatch(
            AccountFormsModule.update({
                loginComponent: isOpen,
                registerComponent: false
            })
        );
    };

    const redirect = (hasOrders: boolean, cartsMerged: boolean) => {
        if (LoginFlow.LOGIN === pageLogic) {
            const queryParams = queryString.parse(window.location.search);

            if (queryParams.redirectTo) {
                return location.assign(queryParams.redirectTo as string);
            }

            if (hasOrders) {
                return location.assign(linkSecondaryTarget);
            }

            return location.assign(linkTarget);
        } else if (LoginFlow.CHECKOUT === pageLogic) {
            if (cartsMerged) {
                return location.assign(linkSecondaryTarget);
            }

            return location.assign(linkTarget);
        } else {
            return;
        }
    };

    const onSubmit = async (email: string, password: string) => {
        if (LoginFlow.CHECKOUT === pageLogic) {
            const validationResult = await validateCheckoutStep(
                CheckoutSteps.login,
                true
            );

            if (validationResult !== CheckoutStepValidationResult.Valid) {
                return;
            }
        }

        login(email, password).then(
            (action: TApiAction<ComponentsCustomer>) => {
                const payload = action.payload;

                if (action.type !== CustomerModule.APIStatus.SUCCESS) {
                    return null;
                }

                if (LoginFlow.LOGIN === pageLogic) {
                    LoginStepTracker.trackSubmit(payload);
                }

                SessionStorage.setItem(
                    StorageKeys.CartsMerged,
                    payload.cartsMerged.toString()
                );
                redirect(payload.hasOrders, payload.cartsMerged);

                return null;
            }
        );
    };

    const onTouched = () => {
        if (LoginFlow.LOGIN === pageLogic) {
            LoginStepTracker.trackStart();
        }
    };

    return (
        <LoginForm
            title={title}
            hasAccountModalUserData={hasAccountModalUserData}
            buttonText={buttonText}
            isOpen={!!accountForms?.loginComponent}
            updateCardToggle={onUpdateCardToggle}
            onSubmit={onSubmit}
            isLoading={isLoadingCustomer || cartLoading}
            onFormTouched={onTouched}
        />
    );
};

export { LoginFormContainer };
